import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FaMapMarkerAlt, FaChevronDown } from 'react-icons/fa';
import useTheme from '../../hooks/useTheme';

const LocationButton = () => {
  const { profile } = useSelector((state) => state.profile);
  const theme = useTheme();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef(null);

  // Get unique locations from StoreGroup
  const getUniqueLocations = () => {
    if (!profile?.StoreGroup?.BranchList) return [];
    
    const uniqueLocations = [];
    const seenLocations = new Set();
    
    profile.StoreGroup.BranchList.forEach((branch, index) => {
      // Skip the first element
      if (index === 0) return;
      
      const locationLower = branch.location?.toLowerCase();
      if (locationLower && !seenLocations.has(locationLower)) {
        seenLocations.add(locationLower);
        uniqueLocations.push(branch);
      }
    });
    
    return uniqueLocations;
  };

  const handleLocationClick = () => {
    console.log('Location clicked');
    console.log('Profile:', profile);
    console.log('StoreGroup:', profile?.StoreGroup);
    console.log('BranchList:', profile?.StoreGroup?.BranchList);
    
    if (profile?.StoreGroup?.BranchList && profile.StoreGroup.BranchList.length > 0) {
      // Toggle modal - close if already open, open if closed
      if (showLocationModal) {
        console.log('Closing modal...');
        setShowLocationModal(false);
      } else {
        console.log('Opening modal...');
        
        // Get button position
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setButtonPosition({
            top: rect.bottom + 4, // 4px below button
            left: rect.left
          });
        }
        
        setShowLocationModal(true);
      }
    } else {
      console.log('No branches available');
    }
  };

  const handleBranchClick = (link) => {
    console.log('Navigating to:', link);
    // Navigate to the link in the same tab
    window.location.href = link;
    setShowLocationModal(false);
  };

  // Close modal when clicking outside
  useEffect(() => {
    if (!showLocationModal) return;

    const handleClickOutside = () => {
      setShowLocationModal(false);
    };

    // Add a small delay to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showLocationModal]);

  return (
    <>
      <div className="flex justify-start relative pb-2">
        <div className="relative">
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLocationClick();
            }}
            type="button"
            className="relative rounded-md px-2 py-1 shadow-md border border-white/30 backdrop-blur-sm hover:scale-105 transition-transform duration-200 cursor-pointer z-10"
            style={{ background: theme.colors.locationBg, pointerEvents: 'auto' }}
          >
          <div className="flex items-center justify-center space-x-1" style={{ pointerEvents: 'none' }}>
            <FaMapMarkerAlt 
              className="drop-shadow-lg animate-pulse" 
              style={{ 
                fontSize: '10px',
                color: theme.colors.locationIcon 
              }} 
            />
            <span 
              className="font-bold poppins drop-shadow-lg tracking-wide" 
              style={{ 
                fontSize: '10px',
                color: theme.colors.locationText 
              }}
            >
              {profile?.name || 'Store'}
            </span>
            {profile?.StoreGroup?.BranchList && profile.StoreGroup.BranchList.length > 1 && (
              <FaChevronDown 
                className="drop-shadow-lg ml-0.5" 
                style={{ 
                  fontSize: '11px',
                  color: theme.colors.locationText,
                  opacity: 0.9
                }} 
              />
            )}
            <div 
              className="w-1.5 h-1.5 rounded-full animate-ping"
              style={{ backgroundColor: theme.colors.locationAccent }}
            ></div>
          </div>
          {/* Decorative elements - smaller */}
          <div 
            className="absolute -top-0.5 -left-0.5 w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: theme.colors.locationAccent }}
          ></div>
          <div 
            className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: theme.colors.locationAccent }}
          ></div>
          <div 
            className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full animate-bounce"
            style={{ backgroundColor: theme.colors.locationAccent }}
          ></div>
          </button>

          {/* Location dropdown - positioned relative to button */}
          {showLocationModal && (
            <div 
              className="fixed bg-white rounded-lg shadow-lg border border-gray-200"
              style={{ 
                zIndex: 40,
                top: `${buttonPosition.top}px`,
                left: `${buttonPosition.left}px`
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-2 space-y-1 min-w-[120px]">
                {getUniqueLocations().map((branch, index) => (
                  <button
                    key={index}
                    onClick={() => handleBranchClick(branch.link)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-primary/10 rounded transition-all duration-200"
                  >
                    <FaMapMarkerAlt className="w-3 h-3 text-primary" />
                    <span className="font-semibold text-gray-800 poppins text-xs whitespace-nowrap">
                      {branch.location}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop to close modal */}
      {showLocationModal && (
        <div 
          className="fixed inset-0 z-[9998]"
          onClick={() => setShowLocationModal(false)}
        />
      )}
    </>
  );
};

export default LocationButton;

